import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Clear as ClearIcon, Search as SearchIcon } from "@mui/icons-material";

export const TABLE_SEARCH_DEBOUNCE_MS = 3000;

/** `blur` / `clickaway`: leaving the field with a valid query (≥2 chars) commits it. */
export type TableSearchApplySource = "debounce" | "enter" | "blur";

export type ExpandableTableSearchProps = {
  value: string;
  onApply: (next: string, source: TableSearchApplySource) => void;
  /** Short hint next to the icon when collapsed. */
  infoText: string;
  placeholder?: string;
  /** Return an error message if invalid, or `null` if OK. */
  validate?: (raw: string) => string | null;
  disabled?: boolean;
  /** Refs to nodes outside this control that should not trigger dismiss (e.g. sibling refresh). */
  ignoreExternalRefs?: React.RefObject<HTMLElement | null>[];
  /** `aria-label` for the collapsed search toggle. */
  ariaLabel?: string;
  /** Minimum trimmed length before debounce / blur can apply a non-empty search (default 2). */
  minSearchLength?: number;
};

const DEFAULT_MIN_LEN = 2;

/**
 * Collapsed: search icon + short info. Expanded: text field with search icon inside.
 * - Debounce / blur (with ≥ min chars): apply when query changed.
 * - Enter on empty: clears search (collapses). Enter with ≥ min chars: searches; field stays expanded. Enter on too-short text: validation error.
 * - Blur / click-away with empty or too-short draft: revert draft to last applied value and collapse.
 * - Blur / click-away with ≥ min chars: apply if changed; keep the field expanded (do not collapse to icon-only).
 */
function ExpandableTableSearch({
  value,
  onApply,
  infoText,
  placeholder,
  validate,
  disabled = false,
  ignoreExternalRefs,
  ariaLabel,
  minSearchLength = DEFAULT_MIN_LEN,
}: ExpandableTableSearchProps) {
  const { t } = useTranslation();
  const searchPlaceholder = placeholder ?? t("common.searchPlaceholder");
  const searchAriaLabel = ariaLabel ?? t("common.openSearch");
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(value);
  const [helper, setHelper] = useState<string | null>(null);
  const appliedThisExpandRef = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expandedRef = useRef(false);
  expandedRef.current = expanded;

  const draftRef = useRef(draft);
  draftRef.current = draft;
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const clearDebounce = useCallback(() => {
    if (debounceTimerRef.current != null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const dismissWithoutCommit = useCallback(() => {
    clearDebounce();
    if (!expandedRef.current) return;
    setExpanded(false);
    if (!appliedThisExpandRef.current) {
      setDraft(valueRef.current);
    }
    appliedThisExpandRef.current = false;
    setHelper(null);
  }, [clearDebounce]);

  const runValidation = useCallback(
    (raw: string) => {
      const t = raw.trim();
      if (t.length > 0 && t.length < minSearchLength) {
        setHelper(`Arama için en az ${minSearchLength} karakter girin.`);
        return false;
      }
      const parentMsg = validate?.(raw) ?? null;
      setHelper(parentMsg);
      return parentMsg == null;
    },
    [minSearchLength, validate],
  );

  const finalizeExit = useCallback(() => {
    clearDebounce();
    if (!expandedRef.current) return;

    const q = draftRef.current.trim();
    const v = valueRef.current.trim();

    if (q.length >= minSearchLength) {
      if (!runValidation(draftRef.current)) {
        setExpanded(false);
        setDraft(valueRef.current);
        setHelper(null);
        appliedThisExpandRef.current = false;
        return;
      }
      if (q !== v) {
        onApply(q, "blur");
      }
      setHelper(null);
      appliedThisExpandRef.current = false;
      return;
    }

    dismissWithoutCommit();
  }, [clearDebounce, dismissWithoutCommit, minSearchLength, onApply, runValidation]);

  useEffect(() => {
    if (!expanded || disabled) return;
    clearDebounce();
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      const d = draftRef.current;
      const q = d.trim();
      if (q.length < minSearchLength) return;
      if (!runValidation(d)) return;
      if (q !== valueRef.current.trim()) {
        onApply(q, "debounce");
      }
    }, TABLE_SEARCH_DEBOUNCE_MS);
    return () => {
      clearDebounce();
    };
  }, [clearDebounce, draft, disabled, expanded, minSearchLength, onApply, runValidation]);

  useEffect(() => {
    if (!expanded || disabled) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (rootRef.current?.contains(t)) return;
      if (ignoreExternalRefs?.some((r) => r.current?.contains(t))) return;
      finalizeExit();
    };
    document.addEventListener("mousedown", onDocMouseDown, true);
    return () => document.removeEventListener("mousedown", onDocMouseDown, true);
  }, [disabled, expanded, finalizeExit, ignoreExternalRefs]);

  const open = () => {
    if (disabled) return;
    appliedThisExpandRef.current = false;
    setExpanded(true);
    setDraft(valueRef.current);
    setHelper(null);
    queueMicrotask(() => {
      inputRef.current?.focus();
    });
  };

  const handleDraftChange = (raw: string) => {
    setDraft(raw);
    runValidation(raw);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      clearDebounce();
      const q = draft.trim();
      if (q.length === 0) {
        if (!runValidation(draft)) return;
        appliedThisExpandRef.current = true;
        onApply("", "enter");
        setExpanded(false);
        appliedThisExpandRef.current = false;
        setHelper(null);
        return;
      }
      if (q.length < minSearchLength) {
        setHelper(`Arama için en az ${minSearchLength} karakter girin.`);
        return;
      }
      if (!runValidation(draft)) return;
      appliedThisExpandRef.current = true;
      onApply(q, "enter");
      appliedThisExpandRef.current = false;
      setHelper(null);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      dismissWithoutCommit();
    }
  };

  const handleBlur = () => {
    window.setTimeout(() => {
      if (!expandedRef.current) return;
      const ae = document.activeElement;
      if (rootRef.current?.contains(ae)) return;
      if (ignoreExternalRefs?.some((r) => r.current?.contains(ae))) return;
      finalizeExit();
    }, 0);
  };

  if (!expanded) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ flex: 1, minWidth: 0, minHeight: 40 }}
      >
        <Tooltip title={searchAriaLabel}>
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={open}
              disabled={disabled}
              aria-label={searchAriaLabel}
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={infoText}>
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ flex: 1, minWidth: 0, cursor: "default" }}
          >
            {infoText}
          </Typography>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Box ref={rootRef} sx={{ flex: 1, minWidth: 0, maxWidth: { xs: "100%", sm: 420 } }}>
      <TextField
        inputRef={inputRef}
        value={draft}
        onChange={(e) => handleDraftChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={searchPlaceholder}
        variant="outlined"
        size="small"
        fullWidth
        disabled={disabled}
        error={Boolean(helper)}
        helperText={helper || undefined}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 20, color: "text.secondary" }} aria-hidden />
            </InputAdornment>
          ),
          endAdornment:
            draft.length > 0 ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={t("common.clearSearch")}
                  edge="end"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    handleDraftChange("");
                    inputRef.current?.focus();
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
        }}
        sx={{
          "& .MuiOutlinedInput-root": { pr: 0.5 },
        }}
      />
    </Box>
  );
}

export default ExpandableTableSearch;
